import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { cn } from '../utils/cn';
import { executiveProtectionService, type ProtectedPerson as APIProtectedPerson, type Activity as APIActivity, type GeofenceAlert, type Venue, type Geofence } from '../services/executive-protection.service';
import { useAuth } from '../stores/auth';
import type { ActivityFormData } from '../components/executive-protection/ActivityForm';

// Extended types for component compatibility
export interface ProtectedPerson extends APIProtectedPerson {
  lastLocation?: string;
  securityDetail?: string;
}

export interface Activity extends Omit<APIActivity, 'protectedPersonId' | 'protectedPersonName'> {
  personId: string;
  personName: string;
  riskAssessment?: 'low' | 'medium' | 'high';
  protectionLevel?: 'standard' | 'enhanced' | 'high';
  notes?: string;
}

export interface GeofenceFormData {
  name: string;
  fenceType: 'SAFE_ZONE' | 'RESTRICTED_ZONE' | 'ALERT_ZONE';
  centerLatitude: number;
  centerLongitude: number;
  radiusMeters: number;
  isActive: boolean;
}

export interface MappedGeofence {
  id: string;
  name: string;
  centerLatitude: number;
  centerLongitude: number;
  radiusMeters: number;
  fenceType: string;
  isActive: boolean;
  alertOnEntry: boolean;
  alertOnExit: boolean;
}

// Context type for child routes
export interface ExecutiveProtectionContextType {
  protectedPersons: ProtectedPerson[];
  activities: Activity[];
  todaysActivities: Activity[];
  geofenceAlerts: GeofenceAlert[];
  venues: Venue[];
  geofences: MappedGeofence[];
  stats: {
    total: number;
    active: number;
    onAssignment: number;
    highRisk: number;
  };
  isLoading: {
    persons: boolean;
    activities: boolean;
    todaysActivities: boolean;
    alerts: boolean;
    venues: boolean;
    geofences: boolean;
  };
  googleMapsApiKey: string;
  handlers: {
    handleCreateActivity: (data: ActivityFormData) => Promise<void>;
    handleGeofenceCreate: (geofence: GeofenceFormData) => Promise<void>;
    handleGeofenceUpdate: (id: string, updates: Partial<GeofenceFormData>) => Promise<void>;
    handleGeofenceDelete: (id: string) => Promise<void>;
  };
}

const tabs = [
  { key: 'dashboard', label: 'Dashboard', path: '/security/executive-protection/dashboard' },
  { key: 'protected-persons', label: 'Protected Persons', path: '/security/executive-protection/protected-persons' },
  { key: 'activities', label: 'Activities', path: '/security/executive-protection/activities' },
  { key: 'geofences', label: 'Geofences', path: '/security/executive-protection/geofences' },
  { key: 'trip-planner', label: 'Trip Planner', path: '/security/executive-protection/trip-planner' },
];

export default function ExecutiveProtectionLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Google Maps API key from environment variables
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'demo-key';

  // Fetch data from API using React Query
  const { data: personsData, isLoading: isLoadingPersons } = useQuery({
    queryKey: ['executive-protection', 'persons'],
    queryFn: () => executiveProtectionService.getProtectedPersons({ page: 1, limit: 100 }),
    refetchInterval: 300000,
    staleTime: 240000,
  });
  const protectedPersons = personsData?.protectedPersons ?? [];

  const { data: activities = [], isLoading: isLoadingMissions } = useQuery({
    queryKey: ['executive-protection', 'activities'],
    queryFn: () => executiveProtectionService.getActivities(),
    refetchInterval: 300000,
    staleTime: 240000,
  });

  const { data: todaysActivities = [], isLoading: isLoadingTodaysActivities } = useQuery({
    queryKey: ['executive-protection', 'activities', 'today'],
    queryFn: () => executiveProtectionService.getTodaysActivities(),
    refetchInterval: 300000,
    staleTime: 240000,
  });

  const { data: geofenceAlerts = [], isLoading: isLoadingAlerts } = useQuery({
    queryKey: ['executive-protection', 'alerts'],
    queryFn: () => executiveProtectionService.getAlerts(),
    refetchInterval: 180000,
    staleTime: 120000,
  });

  const { data: venues = [], isLoading: isLoadingVenues } = useQuery({
    queryKey: ['executive-protection', 'venues'],
    queryFn: () => executiveProtectionService.getVenues(),
    staleTime: 600000,
  });

  const { data: geofences = [], isLoading: isLoadingGeofences } = useQuery({
    queryKey: ['executive-protection', 'geofences'],
    queryFn: () => executiveProtectionService.getGeofences(),
    staleTime: 300000,
  });

  const { data: statsData } = useQuery({
    queryKey: ['executive-protection', 'stats'],
    queryFn: () => executiveProtectionService.getStats(),
    refetchInterval: 300000,
    staleTime: 240000,
  });

  // Mutations
  const createActivityMutation = useMutation({
    mutationFn: (data: Omit<APIActivity, 'id' | 'createdAt' | 'updatedAt'>) => executiveProtectionService.createActivity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'activities'] });
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'stats'] });
    },
  });

  const createGeofenceMutation = useMutation({
    mutationFn: (data: Omit<Geofence, 'id' | 'createdAt'>) => executiveProtectionService.createGeofence(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'geofences'] });
    },
  });

  const updateGeofenceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Geofence> }) => executiveProtectionService.updateGeofence(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'geofences'] });
    },
  });

  const deleteGeofenceMutation = useMutation({
    mutationFn: (id: string) => executiveProtectionService.deleteGeofence(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'geofences'] });
    },
  });

  // Map API activities to component format
  const mappedActivities: Activity[] = activities.map((activity) => ({
    ...activity,
    personId: activity.protectedPersonId,
    personName: activity.protectedPersonName ?? '',
    riskAssessment: activity.riskAssessment as 'low' | 'medium' | 'high' | undefined,
  }));

  const mappedTodaysActivities: Activity[] = todaysActivities.map((activity) => ({
    ...activity,
    personId: activity.protectedPersonId,
    personName: activity.protectedPersonName ?? '',
    riskAssessment: activity.riskAssessment as 'low' | 'medium' | 'high' | undefined,
  }));

  // Map API persons to component format
  const mappedPersons: ProtectedPerson[] = protectedPersons.map((person) => ({
    ...person,
    lastLocation: undefined,
  }));

  // Calculate stats
  const stats = statsData ?? {
    total: mappedPersons.filter(p => p.status === 'active').length,
    active: mappedActivities.filter(m => m.status === 'PLANNED' || m.status === 'IN_PROGRESS').length,
    onAssignment: mappedActivities.filter(m => m.status === 'IN_PROGRESS').length,
    highRisk: mappedPersons.filter(p => ['high', 'critical'].includes(p.riskLevel)).length,
  };

  // Map geofences
  const mappedGeofences: MappedGeofence[] = geofences
    .filter(g => {
      const lat = typeof g.centerLatitude === 'string' ? parseFloat(g.centerLatitude) : g.centerLatitude;
      const lng = typeof g.centerLongitude === 'string' ? parseFloat(g.centerLongitude) : g.centerLongitude;
      return g && !isNaN(lat) && !isNaN(lng);
    })
    .map(g => ({
      id: g.id,
      name: g.name,
      centerLatitude: typeof g.centerLatitude === 'string' ? parseFloat(g.centerLatitude) : g.centerLatitude,
      centerLongitude: typeof g.centerLongitude === 'string' ? parseFloat(g.centerLongitude) : g.centerLongitude,
      radiusMeters: g.radiusMeters,
      fenceType: g.fenceType,
      isActive: g.isActive,
      alertOnEntry: g.alertOnEntry ?? false,
      alertOnExit: g.alertOnExit ?? false,
    }));

  // Handlers
  const handleCreateActivity = async (data: ActivityFormData) => {
    if (!user?.id) {
      console.error('User not authenticated');
      throw new Error('User must be authenticated to create activities');
    }

    await createActivityMutation.mutateAsync({
      protectedPersonId: data.protectedPersonId,
      agentIds: data.agentIds,
      leadAgentId: data.leadAgentId ?? undefined,
      activityType: data.activityType,
      title: data.title,
      description: data.description,
      startDatetime: data.startDatetime,
      endDatetime: data.endDatetime,
      location: data.location,
      venueId: data.venueId,
      status: 'PLANNED',
      riskAssessment: data.riskAssessment,
      securityRequirements: data.securityRequirements,
      closestHospitalName: data.closestHospitalName,
      closestHospitalAddress: data.closestHospitalAddress,
      closestHospitalPhone: data.closestHospitalPhone,
      closestHospitalDistance: data.closestHospitalDistance,
      closestHospitalTravelTime: data.closestHospitalTravelTime,
      closestHospitalCapabilities: data.closestHospitalCapabilities,
      alternativeMedicalFacilities: data.alternativeMedicalFacilities,
      evacuationRoute: data.evacuationRoute,
      onsiteMedicalSupport: data.onsiteMedicalSupport,
      customFields: data.customFields,
      // create-only payload: null collapses to absent (clearing a plan is an edit-flow concern)
      communicationPlan: data.communicationPlan ?? undefined,
      allowPastEnd: data.allowPastEnd,
    });
    navigate('/security/executive-protection/activities');
  };

  const handleGeofenceCreate = async (geofence: GeofenceFormData) => {
    await createGeofenceMutation.mutateAsync({
      name: geofence.name,
      fenceType: geofence.fenceType,
      centerLatitude: geofence.centerLatitude,
      centerLongitude: geofence.centerLongitude,
      radiusMeters: geofence.radiusMeters,
      isActive: geofence.isActive,
      alertOnEntry: geofence.fenceType === 'ALERT_ZONE',
      alertOnExit: geofence.fenceType === 'RESTRICTED_ZONE',
    });
  };

  const handleGeofenceUpdate = async (id: string, updates: Partial<GeofenceFormData>) => {
    await updateGeofenceMutation.mutateAsync({
      id,
      data: {
        name: updates.name,
        isActive: updates.isActive,
        fenceType: updates.fenceType,
        ...(updates.centerLatitude !== undefined && { centerLatitude: updates.centerLatitude }),
        ...(updates.centerLongitude !== undefined && { centerLongitude: updates.centerLongitude }),
        ...(updates.radiusMeters !== undefined && { radiusMeters: updates.radiusMeters }),
      }
    });
  };

  const handleGeofenceDelete = async (id: string) => {
    await deleteGeofenceMutation.mutateAsync(id);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  // Determine active tab
  const getActiveTab = () => {
    const path = location.pathname;
    for (const tab of tabs) {
      if (path === tab.path || path.startsWith(tab.path + '/')) {
        return tab.key;
      }
    }
    return 'protected-persons';
  };

  const activeTab = getActiveTab();

  // Context to pass to child routes
  const contextValue: ExecutiveProtectionContextType = {
    protectedPersons: mappedPersons,
    activities: mappedActivities,
    todaysActivities: mappedTodaysActivities,
    geofenceAlerts,
    venues,
    geofences: mappedGeofences,
    stats,
    isLoading: {
      persons: isLoadingPersons,
      activities: isLoadingMissions,
      todaysActivities: isLoadingTodaysActivities,
      alerts: isLoadingAlerts,
      venues: isLoadingVenues,
      geofences: isLoadingGeofences,
    },
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    handlers: {
      handleCreateActivity,
      handleGeofenceCreate,
      handleGeofenceUpdate,
      handleGeofenceDelete,
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Executive Protection
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Monitor and manage protection for executive personnel
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate('/security/executive-protection/protected-persons/create')}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Executive
            </Button>
            <Button onClick={() => navigate('/security/executive-protection/activities/create')}>
              <PlusIcon className="w-4 h-4 mr-2" />
              New Activity
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-2">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={tab.path}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    activeTab === tab.key
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-100 dark:hover:bg-secondary-800'
                  )}
                >
                  {tab.label}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {stats.total ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Protected Persons
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {stats.active ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Active Activities
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {geofenceAlerts.length}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Alerts Today
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {stats.highRisk ?? 0}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                High Risk
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Child Route Content */}
      <motion.div variants={itemVariants}>
        <Outlet context={contextValue} />
      </motion.div>
    </motion.div>
  );
}
